import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Database, Pencil, Trash2, Copy } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardDescription,
} from "@/components/ui/Card";

interface HmisRecord {
  id: number;
  person_id: number;
  agency_id: number;
  record_type: string;
  assessment_date: string | null;
  data: string | null;
  created_by: number | null;
  created_at: string;
  author_first_name?: string;
  author_last_name?: string;
}

interface HmisListProps {
  personId: string;
}

export function HmisList({ personId }: HmisListProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingRecord, setEditingRecord] = useState<HmisRecord | null>(null);
  const [copyTargetId, setCopyTargetId] = useState("");
  const [showCopyForm, setShowCopyForm] = useState(false);

  const { data, isLoading } = useQuery<{ data: HmisRecord[] }>({
    queryKey: ["hmisRecords", personId],
    queryFn: () => api.get<{ data: HmisRecord[] }>(`/clients/${personId}/hmis`),
    enabled: !!personId,
  });

  const createMutation = useMutation({
    mutationFn: (body: { recordType: string; assessmentDate?: string; data?: Record<string, unknown> }) =>
      api.post(`/clients/${personId}/hmis`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hmisRecords", personId] });
      setShowForm(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number; recordType?: string; assessmentDate?: string; data?: Record<string, unknown> }) =>
      api.put(`/clients/${personId}/hmis/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hmisRecords", personId] });
      setEditingRecord(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/clients/${personId}/hmis/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["hmisRecords", personId] }),
  });

  const copyMutation = useMutation({
    mutationFn: (targetPersonId: number) =>
      api.post(`/clients/${personId}/hmis/copy`, { targetPersonId }),
    onSuccess: () => {
      setShowCopyForm(false);
      setCopyTargetId("");
    },
  });

  const records = data?.data ?? [];

  function parseData(raw: string | null): Record<string, unknown> | null {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">HMIS Records</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="gap-2" onClick={() => setShowCopyForm(!showCopyForm)}>
            <Copy className="h-4 w-4" />
            Copy to Family
          </Button>
          <Button size="sm" className="gap-2" onClick={() => setShowForm(true)}>
            <Plus className="h-4 w-4" />
            Add Record
          </Button>
        </div>
      </div>

      {showCopyForm && (
        <Card>
          <CardContent className="p-4">
            <div className="space-y-3">
              <Input
                label="Target Person ID"
                value={copyTargetId}
                onChange={(e) => setCopyTargetId(e.target.value)}
                placeholder="Enter the person ID to copy HMIS data to..."
              />
              <div className="flex gap-3">
                <Button
                  size="sm"
                  onClick={() => copyMutation.mutate(parseInt(copyTargetId, 10))}
                  loading={copyMutation.isPending}
                  disabled={!copyTargetId}
                >
                  Copy Data
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowCopyForm(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card>
          <CardContent className="p-4">
            <HmisForm
              onSubmit={(d) => createMutation.mutate(d)}
              onCancel={() => setShowForm(false)}
              isLoading={createMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {editingRecord && (
        <Card>
          <CardContent className="p-4">
            <HmisForm
              initial={editingRecord}
              onSubmit={(d) => updateMutation.mutate({ id: editingRecord.id, ...d })}
              onCancel={() => setEditingRecord(null)}
              isLoading={updateMutation.isPending}
            />
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="space-y-3">
          {[1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg border bg-muted" />
          ))}
        </div>
      )}

      {!isLoading && records.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <Database className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">No HMIS records found.</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && records.map((r) => {
        const authorName = r.author_first_name && r.author_last_name
          ? `${r.author_first_name} ${r.author_last_name}` : null;
        const parsedData = parseData(r.data);

        return (
          <Card key={r.id}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-medium capitalize">
                    {r.record_type.replace(/_/g, " ")}
                  </CardTitle>
                  <CardDescription className="mt-0.5">
                    {r.assessment_date ? new Date(r.assessment_date).toLocaleDateString() : new Date(r.created_at).toLocaleDateString()}
                    {authorName && ` | ${authorName}`}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" onClick={() => setEditingRecord(r)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => {
                    if (confirm("Delete this HMIS record?")) deleteMutation.mutate(r.id);
                  }}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            {parsedData && (
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  {Object.entries(parsedData).map(([key, value]) => (
                    <div key={key} className="contents">
                      <dt className="text-muted-foreground capitalize">{key.replace(/_/g, " ")}</dt>
                      <dd className="font-medium">{String(value)}</dd>
                    </div>
                  ))}
                </dl>
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// HMIS Form
// ---------------------------------------------------------------------------

interface HmisFormProps {
  initial?: HmisRecord;
  onSubmit: (data: { recordType: string; assessmentDate?: string; data?: Record<string, unknown> }) => void;
  onCancel: () => void;
  isLoading: boolean;
}

function HmisForm({ initial, onSubmit, onCancel, isLoading }: HmisFormProps) {
  const [recordType, setRecordType] = useState(initial?.record_type ?? "");
  const [assessmentDate, setAssessmentDate] = useState(initial?.assessment_date ?? "");
  const [jsonData, setJsonData] = useState(() => {
    if (!initial?.data) return "";
    try { return JSON.stringify(JSON.parse(initial.data), null, 2); } catch { return initial.data; }
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium text-foreground">Record Type</label>
          <select
            value={recordType}
            onChange={(e) => setRecordType(e.target.value)}
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="">Select type...</option>
            <option value="universal">Universal Data Elements</option>
            <option value="program">Program-Specific</option>
            <option value="entry">Entry Assessment</option>
            <option value="exit">Exit Assessment</option>
            <option value="annual">Annual Assessment</option>
            <option value="update">Update</option>
          </select>
        </div>
        <Input
          label="Assessment Date"
          type="date"
          value={assessmentDate}
          onChange={(e) => setAssessmentDate(e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium text-foreground">Data (JSON)</label>
        <textarea
          value={jsonData}
          onChange={(e) => setJsonData(e.target.value)}
          rows={6}
          placeholder='{"veteranStatus": "no", "disablingCondition": "no", ...}'
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm transition-colors font-mono placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>
      <div className="flex gap-3">
        <Button
          size="sm"
          onClick={() => {
            let parsed: Record<string, unknown> | undefined;
            if (jsonData) {
              try { parsed = JSON.parse(jsonData); } catch { parsed = { raw: jsonData }; }
            }
            onSubmit({
              recordType: recordType || "universal",
              assessmentDate: assessmentDate || undefined,
              data: parsed,
            });
          }}
          loading={isLoading}
          disabled={!recordType}
        >
          {initial ? "Update Record" : "Create Record"}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}
